// composables/postfooter.composable.ts - OPTIMIZED VERSION
import httpService from "@/services/http.service";
import storageService from "@/services/storage.service";
import { ref, computed } from "vue";
import { useReactionStore } from '@/services/reaction.store';
import { LikePostModel, ReactionType } from "../models/likepost.model";
import { UnlikeModel } from "../models/unlike.model";

export const usePostFooter = (sourceId?: string, sourceType?: string) => {
  const reactionStore = useReactionStore();
  const likePostData = ref(new LikePostModel());
  const storageUser = storageService.user;

  if (sourceId && sourceType) {
    likePostData.value.sourceId = sourceId;
    likePostData.value.sourceType = sourceType;
    likePostData.value.postId = sourceId;
  }

  // OPTIMIZED: Use precomputed reaction counts
  const getReactionCounts = computed(() => {
    return reactionStore.getAllReactionCountsForPost(likePostData.value.sourceId!);
  });

  // OPTIMIZED: Generic function to check if any user has a specific reaction
  const hasReactionType = (reactionType: any) => {
    const counts = getReactionCounts.value;
    return (counts[reactionType] || 0) > 0;
  };

  // OPTIMIZED: Individual reaction checkers
  const showLikeIcon = computed(() => hasReactionType('like'));
  const showHeartIcon = computed(() => hasReactionType('heart'));
  const showAppreciatedIcon = computed(() => hasReactionType('appreciated'));
  const showKissIcon = computed(() => hasReactionType('kiss'));
  const showAllgreenIcon = computed(() => hasReactionType('allgreen'));
  const showAngrybullIcon = computed(() => hasReactionType('angrybull'));
  const showBloodbathIcon = computed(() => hasReactionType('bloodbath'));
  const showCrapmineIcon = computed(() => hasReactionType('crapmine'));
  const showCrashingdownIcon = computed(() => hasReactionType('crashingdown'));
  const showCryptomarketIcon = computed(() => hasReactionType('cryptomarket'));
  const showDeadweightIcon = computed(() => hasReactionType('deadweight'));
  const showDiamondhandIcon = computed(() => hasReactionType('diamondhand'));
  const showDownchartIcon = computed(() => hasReactionType('downchart'));
  const showFeelingsickIcon = computed(() => hasReactionType('feelingsick'));
  const showGoldmineIcon = computed(() => hasReactionType('goldmine'));
  const showKingofthemarketIcon = computed(() => hasReactionType('kingofthemarket'));
  const showQueenofthemarketIcon = computed(() => hasReactionType('queenofthemarket'));
  const showSadbullIcon = computed(() => hasReactionType('sadbull'));
  const showShockIcon = computed(() => hasReactionType('shock'));
  const showShockedIcon = computed(() => hasReactionType('shocked'));
  const showSmirkingbullIcon = computed(() => hasReactionType('smirkingbull'));
  const showTothemoodIcon = computed(() => hasReactionType('tothemood'));
  const showUpchartIcon = computed(() => hasReactionType('upchart'));

  // OPTIMIZED: Check if current user has reacted
  const isPostLiked = computed(() => {
    try {
      return reactionStore.hasUserReacted(
        likePostData.value.sourceId!,
        likePostData.value.sourceType!
      );
    } catch (e) {
      return false;
    }
  });

  // OPTIMIZED: Get current user's reaction type
  const getUserReactionType = computed(() => {
    return reactionStore.getUserReactionType(
      likePostData.value.sourceId!,
      likePostData.value.sourceType!
    );
  });

  const intialData = (sourceId: string, sourceType: string, postId?: string) => {
    likePostData.value.sourceId = sourceId;
    likePostData.value.sourceType = sourceType;
    likePostData.value.postId = postId;
  };

  const likePost = async (event: any, reactionType: ReactionType) => {
    likePostData.value.reactionType = reactionType;
    try {
      console.log("Liking post with data:", likePostData.value);
      await reactionStore.likePost(likePostData.value);
    } catch (err) {
      console.error("Error liking post:", err);
    }
  };

  const unlikePost = async (event: any) => {
    try {
      const unlikePostModel = new UnlikeModel();
      unlikePostModel.sourceId = likePostData.value.sourceId;
      unlikePostModel.sourceType = likePostData.value.sourceType;
      await reactionStore.unlikePost(unlikePostModel);
    } catch (err) {
      console.error("Error unliking post:", err);
    }
  };

  return {
    likePostData,
    storageUser,
    intialData,
    likePost,
    unlikePost,
    isPostLiked,
    getUserReactionType,
    hasReactionType,
    showLikeIcon,
    showHeartIcon,
    showAppreciatedIcon,
    showKissIcon,
    showAllgreenIcon,
    showAngrybullIcon,
    showBloodbathIcon,
    showCrapmineIcon,
    showCrashingdownIcon,
    showCryptomarketIcon,
    showDeadweightIcon,
    showDiamondhandIcon,
    showDownchartIcon,
    showFeelingsickIcon,
    showGoldmineIcon,
    showKingofthemarketIcon,
    showQueenofthemarketIcon,
    showSadbullIcon,
    showShockIcon,
    showShockedIcon,
    showSmirkingbullIcon,
    showTothemoodIcon,
    showUpchartIcon
  };
};